/**
 * @fileoverview PostHog Hooks & Utilities
 * @module utils/posthog/hooks
 *
 * Purpose: Hooks and utility functions for PostHog analytics
 * - Hook to access PostHog client from context
 * - Hook to identify authenticated users with person properties
 */

import { useContext, useEffect, useRef } from "react";
import { useQuery } from "convex/react";
import { api } from "@cvx/_generated/api";
import { PostHogContext, type PostHogClient } from "./context";

/**
 * Hook to access PostHog client from context
 *
 * @returns PostHog client instance or null if not initialized
 *
 * @example
 * const posthog = usePostHog();
 *
 * // Track pageview
 * posthog?.capture("$pageview", { $current_url: "/" });
 *
 * // Track custom event
 * posthog?.capture("button_clicked", { location: "hero" });
 */
export function usePostHog(): PostHogClient {
  return useContext(PostHogContext);
}

/**
 * Identifies the authenticated user in PostHog with person properties.
 * Creates a person profile with name, email, and organisation so that
 * sessions are attributable and cohorts can be built in PostHog.
 *
 * Must be rendered inside both PostHogProvider and an authenticated context
 * (i.e. after Convex auth is confirmed). Re-identifies when user or org
 * data changes; no-ops when data hasn't changed.
 *
 * @example
 * // In an auth-protected layout:
 * usePostHogIdentify();
 */
export function usePostHogIdentify(): void {
  const posthog = usePostHog();
  const user = useQuery(api.users.queries.getCurrentUser);
  const orgs = useQuery(api.organizations.queries.getUserOrganizations);
  const lastIdentifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog || !user) return;

    const primaryOrg = orgs?.[0];
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ");

    const fingerprint = [
      user._id,
      user.email,
      name,
      primaryOrg?.name,
      user.subscription?.planKey,
    ].join("|");

    if (fingerprint === lastIdentifiedRef.current) return;

    posthog.identify(user._id, {
      email: user.email,
      name: name || undefined,
      first_name: user.firstName,
      last_name: user.lastName,
      organisation: primaryOrg?.name,
      organisation_role: primaryOrg?.role,
      plan: user.subscription?.planKey,
      system_role: user.systemRole,
    });

    if (primaryOrg) {
      posthog.group("organisation", primaryOrg._id, {
        name: primaryOrg.name,
        slug: primaryOrg.slug,
      });
    }

    lastIdentifiedRef.current = fingerprint;
  }, [posthog, user, orgs]);
}
